import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import dayjs from 'dayjs';
import { $initialCurrenciesAvailableStore, $dashboardAccountSummaryStore, $initialAccountsStore } from '@lib/store';
import { AccountUtils, DateUtils } from '@shared/utils';
import { AccountType } from '@shared/api/rest/accounts';
import { PaymentItems } from './paymentItems';
import { MonthlyTransaction } from './monthlyTransaction';
import { YearlyTransaction } from './yearlyTransaction';
import { NoData, SearchInput, Select, Spinner, Text } from '@shared/widgets';

const CURRENT_MONTH = DateUtils.getCurrentMonth();

export const PaymentTab = () => {
  const accountStore = useUnit($initialAccountsStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const dashboardsAccountSummaryStore = useUnit($dashboardAccountSummaryStore.store);

  const [searchText, setSearchText] = useState<string>('');
  const [currency, setCurrency] = useState<string>();

  useEffect(() => {
    $dashboardAccountSummaryStore.events.update({ Currency: currency });
  }, []);

  const paymentAccounts = useMemo(() => accountStore.data.filter((account) => account?.type === AccountType.Payment), [accountStore.data]);
  const minimalDate = useMemo(() => dayjs.min(paymentAccounts.map((acc) => dayjs(acc.dateCreated)))?.format(), [paymentAccounts]);
  const filteredAccounts = useMemo(
    () => AccountUtils.filterDashboardAccounts(dashboardsAccountSummaryStore.data, { searchText, currency }),
    [dashboardsAccountSummaryStore.data, searchText, currency],
  );
  const groupedAccounts = useMemo(() => AccountUtils.groupedDashboardAccounts(filteredAccounts), [filteredAccounts]);

  if (dashboardsAccountSummaryStore.loading) return <Spinner />;
  return (
    <>
      <div className='flex flex-col gap-6'>
        <div className='flex justify-between '>
          <Text size='2xl'>
            Accounts Summary
            <span className='text-pearl-dark-gray ml-2'>{CURRENT_MONTH}</span>
          </Text>
          <div className='flex gap-4'>
            <SearchInput className='flex-grow flex-shrink-0 flex-basis-0' value={searchText} onChange={setSearchText} />
            <Select
              className='min-w-[160px]'
              items={currenciesAvailableStore.data}
              nullValue={{ isEnable: true, text: 'All currencies' }}
              value={currency}
              onChange={(val) => setCurrency(val!)}
            />
          </div>
        </div>
        <div className='flex flex-col gap-6'>
          {groupedAccounts.length > 0 ? (
            groupedAccounts.map((group, idx) => (
              <PaymentItems key={`${idx}_${group.mainAccount.id}`} mainAccount={group.mainAccount} subAccounts={group.subAccounts} />
            ))
          ) : (
            <NoData />
          )}
        </div>
      </div>
      <MonthlyTransaction minimalYear={dayjs(minimalDate).year()} minimalMonth={dayjs(minimalDate).month() + 1} />
      <YearlyTransaction minimalYear={dayjs(minimalDate).year()} />
    </>
  );
};
